import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Scale, BadgeCheck, Flame, FileCheck, Droplet, RefreshCcw, CheckCircle, Send, ArrowRight } from 'lucide-react';

const EquipmentSelection = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    email: '',
    facilityType: '',
    equipmentCategory: '',
    applicationPurpose: '',
    capacityRequirements: '',
    fuelType: '',
    emissionLimits: '',
    spaceConstraints: '',
    budgetRange: '',
    reliabilityNeeds: '',
    maintenanceCapability: '',
    specificRequirements: '',
    additionalInfo: ''
  });

  const equipmentCategories = [
    { value: '', label: 'Select equipment category' },
    { value: 'power-generation', label: 'Power Generation Equipment' },
    { value: 'emission-control', label: 'Emission Control Systems' },
    { value: 'monitoring', label: 'Monitoring Equipment' },
    { value: 'process-equipment', label: 'Process Equipment' },
    { value: 'fuel-handling', label: 'Fuel Handling Systems' },
    { value: 'ventilation', label: 'Ventilation Systems' },
    { value: 'other', label: 'Other Equipment' }
  ];

  const facilityTypes = [
    { value: '', label: 'Select facility type' },
    { value: 'power-plant', label: 'Power Plant' },
    { value: 'oil-gas', label: 'Oil & Gas Facility' },
    { value: 'military', label: 'Military Installation' },
    { value: 'seafood', label: 'Seafood Processing' },
    { value: 'mining', label: 'Mining Operation' },
    { value: 'industrial', label: 'Industrial Facility' },
    { value: 'other', label: 'Other' }
  ];

  const serviceDeliverables = [
    {
      title: 'PTE Minimization',
      description: 'Right-sizing equipment to keep potential emissions below Title V thresholds.',
      icon: Scale
    },
    {
      title: 'EPA Tier Standards',
      description: 'Navigating Tier 4 Interim vs. Final requirements for remote Alaska grids (CI/SI RICE).',
      icon: BadgeCheck
    },
    {
      title: 'Low-NOx Technologies',
      description: 'Evaluation of Low-NOx burners and retrofit kits for heaters and boilers.',
      icon: Flame
    },
    {
      title: 'Vendor Specification Review',
      description: 'Ensuring manufacturer guarantees match your permit limits before purchase orders are signed.',
      icon: FileCheck
    },
    {
      title: 'Fuel Strategy',
      description: 'Balancing fuel efficiency curves against emission limits for remote power generation.',
      icon: Droplet
    },
    {
      title: 'Retrofit vs. Replace',
      description: 'Cost-benefit analysis of upgrading existing units versus new capital acquisition.',
      icon: RefreshCcw
    }
  ];

  const benefits = [
    'Avoid expensive add-on controls (SCR/Oxidizers)',
    'Ensure operational flexibility in cold climates',
    'Reduce fuel consumption and logistics costs',
    'Meet NSPS/NESHAP standards without Major Source permitting',
    'Optimize capital expenditure with right-sized equipment',
    'Navigate EPA Tier requirements for Alaska operations'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services/equipment-selection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        alert('Thank you! Your equipment selection inquiry has been submitted successfully. We will contact you with recommendations.');
        // Reset form
        setFormData({
          firstName: '', lastName: '', company: '', phone: '', email: '',
          facilityType: '', equipmentCategory: '', applicationPurpose: '', capacityRequirements: '',
          fuelType: '', emissionLimits: '', spaceConstraints: '', budgetRange: '',
          reliabilityNeeds: '', maintenanceCapability: '', specificRequirements: '', additionalInfo: ''
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your inquiry. Please try again or contact us directly.');
    }
  };

  const selectionCriteria = [
    {
      title: 'Regulatory Compliance',
      description: 'Equipment must meet all applicable emission standards and performance requirements.',
      considerations: ['EPA standards', 'Alaska state requirements', 'Industry-specific regulations', 'Best Available Control Technology']
    },
    {
      title: 'Performance Requirements',
      description: 'Equipment capacity and efficiency must match operational needs.',
      considerations: ['Power output/capacity', 'Efficiency ratings', 'Load flexibility', 'Turndown ratios']
    },
    {
      title: 'Environmental Conditions',
      description: 'Equipment must perform reliably in Alaska\'s challenging climate.',
      considerations: ['Cold weather operation', 'Remote location suitability', 'Winterization needs', 'Arctic-rated components']
    },
    {
      title: 'Cost Analysis',
      description: 'Total cost of ownership including capital, operation, and maintenance.',
      considerations: ['Initial capital cost', 'Operating costs', 'Maintenance expenses', 'Fuel efficiency']
    },
    {
      title: 'Reliability & Availability',
      description: 'Equipment reliability is critical for remote Alaska operations.',
      considerations: ['Proven track record', 'MTBF ratings', 'Spare parts availability', 'Service support']
    },
    {
      title: 'Installation & Integration',
      description: 'Practical considerations for installation and system integration.',
      considerations: ['Space requirements', 'Utility needs', 'Integration complexity', 'Installation timeline']
    }
  ];

  const equipmentTypes = [
    {
      category: 'Power Generation',
      description: 'Generators and prime movers for reliable Alaska power production.',
      examples: ['Diesel generators', 'Natural gas engines', 'Turbines', 'Combined heat & power (CHP) systems']
    },
    {
      category: 'Emission Control',
      description: 'Technologies to reduce and control air pollutant emissions.',
      examples: ['Catalytic converters', 'Selective catalytic reduction (SCR)', 'Particulate filters', 'Oxidation catalysts']
    },
    {
      category: 'Monitoring Equipment',
      description: 'Systems for continuous and periodic emissions monitoring.',
      examples: ['CEMS (Continuous Emission Monitoring)', 'Portable analyzers', 'Data acquisition systems', 'Opacity monitors']
    },
    {
      category: 'Process Equipment',
      description: 'Core industrial process equipment for various applications.',
      examples: ['Boilers and heaters', 'Compressors', 'Pumps', 'Heat exchangers']
    }
  ];

  return (
    <div className="min-h-screen" data-testid="equipment-selection-page">
      {/* Hero Section */}
      <section
        className="relative bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(https://images.unsplash.com/photo-1581092160562-40aa08e78837?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwZXF1aXBtZW50fGVufDB8fHx8MTc2MTEwMTE5MXww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Equipment Selection & EPA Tier Strategy
            </h1>
            <p className="text-xl text-gray-100 mb-8">
              Capital-efficient sizing guidance for engines, boilers, and generators. We help you select equipment that meets NSPS/NESHAP standards without triggering Major Source permitting.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#contact-form"
                className="inline-flex items-center px-6 py-3 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors shadow-lg"
              >
                Request Sizing Analysis
                <Send className="ml-2" size={20} />
              </a>
              <a
                href="#key-deliverables"
                className="inline-flex items-center px-6 py-3 bg-white text-blue-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              >
                View Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Selecting the Right Equipment for Your Operation</h2>
            <p className="text-lg text-gray-600 mb-4 leading-relaxed">
              Choosing the right equipment is critical for meeting environmental compliance requirements while maintaining operational efficiency and reliability. In Alaska's challenging environment, equipment selection becomes even more important.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Our equipment selection service evaluates your specific needs against regulatory requirements, operational conditions, and budget constraints to recommend optimal equipment solutions. We consider total cost of ownership, reliability in Alaska conditions, and long-term compliance.
            </p>
          </div>
        </div>
      </section>

      {/* Equipment Selection Criteria */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Equipment Selection Criteria</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {selectionCriteria.map((criteria, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md border-t-4 border-blue-700">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{criteria.title}</h3>
                <p className="text-gray-600 mb-4">{criteria.description}</p>
                <ul className="space-y-2">
                  {criteria.considerations.map((item, idx) => (
                    <li key={idx} className="flex items-start text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-blue-700 mr-2 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment Categories We Evaluate */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Equipment Categories We Evaluate</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {equipmentTypes.map((type, index) => (
              <div key={index} className="bg-blue-50 p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{type.category}</h3>
                <p className="text-gray-600 mb-4">{type.description}</p>
                <div className="space-y-1">
                  {type.examples.map((example, idx) => (
                    <div key={idx} className="text-sm text-gray-700 pl-4">
                      • {example}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Alaska-Specific Equipment Considerations */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Alaska-Specific Equipment Considerations</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Extreme Cold Operation</h3>
                <p className="text-gray-600">Equipment must function reliably in temperatures down to -60°F. This requires special materials, insulation, and heating systems.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Remote Locations</h3>
                <p className="text-gray-600">Limited access requires highly reliable equipment with extended service intervals and readily available spare parts.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Fuel Logistics</h3>
                <p className="text-gray-600">Fuel availability and cost vary significantly. Equipment must be flexible to use available fuel sources efficiently.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Maintenance Support</h3>
                <p className="text-gray-600">Local service and parts availability is limited. Equipment should be maintainable with available resources.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Deliverables - Amber Jewel Icons */}
      <section id="key-deliverables" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Key Deliverables
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Strategic equipment sizing to optimize capital costs and regulatory compliance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {serviceDeliverables.map((deliverable, index) => {
              const IconComponent = deliverable.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-xl shadow-md hover:shadow-xl transition-shadow">
                  {/* Jewel Style Icon - Amber */}
                  <div className="bg-amber-500 w-16 h-16 rounded-lg flex items-center justify-center shadow-lg mb-4">
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{deliverable.title}</h3>
                  <p className="text-gray-600">{deliverable.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why It Matters - Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why It Matters
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Strategic equipment selection delivers tangible operational and financial benefits
            </p>
          </div>
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-1" />
                <span className="text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section id="contact-form" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="bg-amber-600 text-white p-8 rounded-t-xl">
              <div className="flex items-center mb-4">
                <Send className="w-10 h-10 mr-4" />
                <div>
                  <h2 className="text-3xl font-bold">Request Equipment Sizing Analysis</h2>
                  <p className="text-amber-100 mt-2">Tell us about your equipment needs for expert sizing recommendations</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-b-xl shadow-xl border border-gray-200">
              {/* Contact Information */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company *</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Equipment Needs */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Equipment Requirements</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Facility Type</label>
                    <select
                      name="facilityType"
                      value={formData.facilityType}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    >
                      {facilityTypes.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Equipment Category</label>
                    <select
                      name="equipmentCategory"
                      value={formData.equipmentCategory}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    >
                      {equipmentCategories.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Application Purpose</label>
                  <textarea
                    name="applicationPurpose"
                    value={formData.applicationPurpose}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Describe what the equipment will be used for"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  ></textarea>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Capacity Requirements</label>
                    <input
                      type="text"
                      name="capacityRequirements"
                      value={formData.capacityRequirements}
                      onChange={handleChange}
                      placeholder="e.g., 2 MW, 500 CFM"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fuel Type</label>
                    <input
                      type="text"
                      name="fuelType"
                      value={formData.fuelType}
                      onChange={handleChange}
                      placeholder="e.g., Natural gas, diesel"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Emission Limits or Compliance Requirements</label>
                  <textarea
                    name="emissionLimits"
                    value={formData.emissionLimits}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Known emission limits or permit requirements"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  ></textarea>
                </div>
              </div>

              {/* Site Constraints */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Site & Operational Constraints</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Space Constraints</label>
                  <input
                    type="text"
                    name="spaceConstraints"
                    value={formData.spaceConstraints}
                    onChange={handleChange}
                    placeholder="Available space dimensions or limitations"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Budget Range</label>
                    <input
                      type="text"
                      name="budgetRange"
                      value={formData.budgetRange}
                      onChange={handleChange}
                      placeholder="Approximate budget"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Reliability Needs</label>
                    <input
                      type="text"
                      name="reliabilityNeeds"
                      value={formData.reliabilityNeeds}
                      onChange={handleChange}
                      placeholder="e.g., 24/7 operation, backup required"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Maintenance Capability</label>
                  <textarea
                    name="maintenanceCapability"
                    value={formData.maintenanceCapability}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Local service capabilities, in-house maintenance staff, etc."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  ></textarea>
                </div>
              </div>

              {/* Specific Requirements */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Specific Requirements or Concerns <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="specificRequirements"
                  value={formData.specificRequirements}
                  onChange={handleChange}
                  required
                  rows="4"
                  placeholder="e.g., Equipment specifications, performance requirements, emission control needs, BACT considerations, compliance constraints, installation timeline, budget limitations, etc."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                ></textarea>
              </div>

              {/* Additional Information */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
                <textarea
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Any other details, specific equipment preferences, or questions"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                ></textarea>
              </div>

              {/* Submit Button */}
              <div className="flex items-center justify-center">
                <button
                  type="submit"
                  className="inline-flex items-center px-8 py-4 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors shadow-lg"
                >
                  Submit Sizing Request
                  <Send className="ml-2" size={20} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Optimize Equipment Selection & Capital Costs
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Right-size your engines, boilers, and generators to meet EPA standards efficiently
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-all shadow-lg hover:shadow-2xl transform hover:scale-105"
          >
            Get Expert Sizing Guidance
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default EquipmentSelection;